import React, { useEffect, useState, useRef } from 'react';
import { Grid, TextField, Typography, Paper, MenuItem } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { cargarCatalogo } from '../../components/CargarCatalogoBienestar';
import { fetchDatosUsuarioBienestar } from '../../services/bienestarDetalleService';
import { GENERO, ESTADO_CIVIL, INTERESADO_BIENESTAR_ROL } from '../../../helper';
import { useFormValidation } from './useFormValidation';
  

/* =======================
    STEP 1 — Datos Personales
   ======================= */
function DatosEntrevistado({ state, onChange, solicitud }) {
    const solicitudMap = {
        nombre_completo:
            (solicitud?.usuario_registro
                ? `${solicitud?.usuario_registro?.primer_nombre || ''} ${solicitud?.usuario_registro?.segundo_nombre || ''} ${solicitud?.usuario_registro?.primer_apellido || ''} ${solicitud?.usuario_registro?.segundo_apellido || ''}`.trim()
                : (solicitud?.nombre_completo ?? solicitud?.nombre ?? '')),
        documento_identidad:
            (solicitud?.usuario_registro?.identificacion ?? solicitud?.documento_identidad ?? solicitud?.documento_id ?? ''),
        correo_electronico:
            (solicitud?.usuario_registro?.correo_personal ?? solicitud?.correo_electronico ?? solicitud?.correo ?? ''),
        programa:
            (solicitud?.programa?.nombre ?? solicitud?.programa ?? solicitud?.programa_dependencia ?? ''),
    };

    const safeValue = (field) => {
        const valor = state?.[field]?.valor;
        if (valor !== undefined && valor !== '') {
            return valor;
        }
        if (solicitudMap[field] !== undefined && solicitudMap[field] !== null && solicitudMap[field] !== '') {
            return solicitudMap[field];
        }
        return '';
    };
    const safeError = (field) => (state?.[field]?.error || '');
    const conError = (msg) => !!msg;
    const [genero, setGenero] = useState([]);
    const [estadoCivil, setEstadoCivil] = useState([]); 
    const [interesado, setInteresado] = useState([]);
    const [programasOpciones, setProgramasOpciones] = useState([]);
    const inicializadoRef = useRef(false);
    
    const OPCIONES_SINO = [
        { value: 'Sí', label: 'Sí' },
        { value: 'No', label: 'No' }
    ];

    useEffect(() => {
    const cargarTodo = async () => {
        await Promise.all([
            cargarCatalogo(GENERO, setGenero),
            cargarCatalogo(ESTADO_CIVIL, setEstadoCivil),
            cargarCatalogo(INTERESADO_BIENESTAR_ROL, setInteresado),
        ]);

        const programasSolicitud = solicitud?.usuario_registro?.programas || solicitud?.programas || [];
        if (Array.isArray(programasSolicitud) && programasSolicitud.length > 0) {
            const opcionesSolicitud = programasSolicitud.map((prog) => ({
                value: prog.id_relacion || prog.id || prog.codigo || prog.value || prog.nombre_relacion || prog.nombre,
                label: prog.nombre_relacion || prog.nombre || prog.label || '',
            })).filter((op) => op.label);

            setProgramasOpciones(opcionesSolicitud);
            return;
        }

        const resp = await fetchDatosUsuarioBienestar();
        const opcionesApi = Array.isArray(resp?.programas)
            ? resp.programas.map((prog) => ({
                value: prog.id_relacion || prog.id || prog.codigo || prog.value || prog.nombre_relacion || prog.nombre,
                label: prog.nombre_relacion || prog.nombre || prog.label || '',
            })).filter((op) => op.label)
            : [];
        const prog = solicitud?.programa;
        const opcionesEstudiante = (prog && typeof prog === 'object' && (prog.nombre || prog.nombre_relacion))
            ? [{ value: prog.id_relacion || prog.id, label: prog.nombre_relacion || prog.nombre }]
            : [];

        setProgramasOpciones([
            ...opcionesEstudiante,
            ...opcionesApi.filter((op) => !opcionesEstudiante.some((oe) => String(oe.value) === String(op.value))),
        ]);
    };
        cargarTodo();
    }, [solicitud]);

    useEffect(() => {
        if (solicitud && !inicializadoRef.current) {
            inicializadoRef.current = true;
            
            const camposAInicializar = [
                { campo: 'nombre_completo', valor: solicitudMap.nombre_completo },
                { campo: 'documento_identidad', valor: solicitudMap.documento_identidad },
                { campo: 'correo_electronico', valor: solicitudMap.correo_electronico },
                { campo: 'programa', valor: solicitudMap.programa },
            ];

            camposAInicializar.forEach(({ campo, valor }) => {
                if (valor && !state?.[campo]?.valor) {
                    onChange({ target: { name: campo, value: valor } });
                }
            });
        }
    }, [solicitud]);

    const calcularEdad = (fechaNacimiento) => {
        if (!fechaNacimiento) return '';
        const hoy = new Date();
        const nacimiento = new Date(fechaNacimiento);
        let edad = hoy.getFullYear() - nacimiento.getFullYear();
        const m = hoy.getMonth() - nacimiento.getMonth();
        if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) {
            edad--;
        }
        return edad >= 0 ? edad : '';
    };

    const edadCalculada = calcularEdad(safeValue('fecha_nacimiento'));

    return (
        <Grid container spacing={3}>

            <Grid item xs={12} md={6}>
                <TextField
                    disabled
                    fullWidth
                    label="Nombre Completo"
                    name="nombre_completo"
                    InputLabelProps={{ shrink: true }}
                    value={safeValue('nombre_completo')}
                    onChange={onChange}
                    error={conError(safeError('nombre_completo'))}
                    helperText={safeError('nombre_completo')}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    disabled
                    fullWidth
                    label="Documento de Identidad"
                    name="documento_identidad"
                    InputLabelProps={{ shrink: true }}
                    value={safeValue('documento_identidad')}
                    onChange={onChange}
                    error={conError(safeError('documento_identidad'))}
                    helperText={safeError('documento_identidad')}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    label="Lugar de Expedición del Documento de Identidad"
                    name="lugar_expedicion"
                    InputLabelProps={{ shrink: true }}
                    value={safeValue('lugar_expedicion')}
                    onChange={onChange}
                    error={conError(safeError('lugar_expedicion'))}
                    helperText={safeError('lugar_expedicion')}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    type="date"
                    label="Fecha de Nacimiento"
                    name="fecha_nacimiento"
                    InputLabelProps={{ shrink: true }}
                    value={safeValue('fecha_nacimiento')}
                    onChange={onChange}
                    error={conError(safeError('fecha_nacimiento'))}
                    helperText={safeError('fecha_nacimiento')}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    type="number"
                    label="Edad"
                    name="edad"
                    value={edadCalculada}
                    disabled
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    select
                    label="Programa / Dependencia"
                    name="programa"
                    value={safeValue('programa')}
                    onChange={onChange}
                    error={conError(safeError('programa'))}
                    helperText={safeError('programa')}
                >
                    {programasOpciones.map((opcion) => (
                        <MenuItem key={opcion.value} value={opcion.label}>
                            {opcion.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    label="Semestre"
                    name="semestre"
                    value={safeValue('semestre')}
                    onChange={onChange}
                    error={conError(safeError('semestre'))}
                    helperText={safeError('semestre')}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField
                    select
                    fullWidth
                    label="Estado Civil"
                    name="estado_civil"
                    value={safeValue('estado_civil')}
                    onChange={onChange}
                    error={conError(safeError('estado_civil'))}
                    helperText={safeError('estado_civil')}
                >
                    {estadoCivil.map((opcion) => (
                        <MenuItem key={opcion.value} value={opcion.label}>
                        {opcion.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>


            <Grid item xs={12} md={4}>
                <TextField
                    select
                    fullWidth
                    label="Género"
                    name="genero"
                    value={safeValue('genero')}
                    onChange={onChange}
                    error={conError(safeError('genero'))}
                    helperText={safeError('genero')}
                >
                    {genero.map((opcion) => (
                        <MenuItem key={opcion.value} value={opcion.label}>
                        {opcion.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    select
                    label="Pertenece a algún grupo priorizado"
                    name="pertenece_grupo_priorizado"
                    value={safeValue('pertenece_grupo_priorizado')}
                    onChange={onChange}
                    error={conError(safeError('pertenece_grupo_priorizado'))}
                    helperText={safeError('pertenece_grupo_priorizado')}
                >
                    {OPCIONES_SINO.map((opcion) => (
                        <MenuItem key={opcion.value} value={opcion.value}>
                            {opcion.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            {safeValue('pertenece_grupo_priorizado') === 'Sí' && (
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label="¿Cuál?"
                        name="nombre_grupo_priorizado"
                        value={safeValue('nombre_grupo_priorizado')}
                        onChange={onChange}
                        placeholder="Especifique el grupo"
                        error={conError(safeError('nombre_grupo_priorizado'))}
                        helperText={safeError('nombre_grupo_priorizado')}
                    />
                </Grid>
            )}

            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    label="Número de contacto"
                    name="numero_contacto"
                    value={safeValue('numero_contacto')}
                    onChange={onChange}
                    error={conError(safeError('numero_contacto'))}
                    helperText={safeError('numero_contacto')}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    label="Correo electrónico"
                    name="correo_electronico"
                    type="email"
                    value={safeValue('correo_electronico')}
                    onChange={onChange}
                    error={conError(safeError('correo_electronico'))}
                    helperText={safeError('correo_electronico')}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    label="Dirección de residencia"
                    name="direccion_residencia"
                    value={safeValue('direccion_residencia')}
                    onChange={onChange}
                    error={conError(safeError('direccion_residencia'))}
                    helperText={safeError('direccion_residencia')}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField
                    select
                    fullWidth
                    label="Rol de la persona"
                    name="rol_persona"
                    value={safeValue('rol_persona')}
                    onChange={onChange}
                    error={conError(safeError('rol_persona'))}
                    helperText={safeError('rol_persona')}
                >
                    {interesado.map((opcion) => (
                        <MenuItem key={opcion.value} value={opcion.label}>
                        {opcion.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    type="date"
                    label="Fecha de la entrevista"
                    name="fecha_entrevista"
                    InputLabelProps={{ shrink: true }}
                    value={safeValue('fecha_entrevista')}
                    onChange={onChange}
                    error={conError(safeError('fecha_entrevista'))}
                    helperText={safeError('fecha_entrevista')}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    type="date"
                    label="Fecha de revisión y firma de la transcripción"
                    name="fecha_firma_transcripcion"
                    InputLabelProps={{ shrink: true }}
                    value={safeValue('fecha_firma_transcripcion')}
                    onChange={onChange}
                    error={conError(safeError('fecha_firma_transcripcion'))}
                    helperText={safeError('fecha_firma_transcripcion')}
                />
            </Grid>
            <Grid item xs={12} md={4}></Grid>
        </Grid>
    );
}

/* =======================
    STEP 3 — Relato de Hechos (Se mantiene igual)
   ======================= */
function RelatoHechos({ state, onChange }) {
    const { safeValue, safeError, conError } = useFormValidation(state);
    const theme = useTheme();

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 'bold' }}>
                    Entrevistador/a: ¿Relátame cómo sucedieron los hechos?
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={5}
                    variant="outlined"
                    label="Entrevistado/a:"
                    name="relato_hechos"
                    value={safeValue('relato_hechos')}
                    onChange={onChange}
                    error={conError(safeError('relato_hechos'))}
                    helperText={safeError('relato_hechos')}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Número de cuartillas"
                    name="numero_cuartillas"
                    type="number"
                    value={safeValue('numero_cuartillas')}
                    onChange={onChange}
                    error={conError(safeError('numero_cuartillas'))}
                    helperText={safeError('numero_cuartillas')}
                />
            </Grid>
            
            <Grid item xs={12}>
                <Paper elevation={0} style={{ padding: 15, backgroundColor: '#f9f9f9', borderLeft: `4px solid ${theme.palette.primary.main}`}}>
                    <Typography variant="caption" style={{ display: 'block' }}>
                        Se finaliza la entrevista haciendo énfasis frente a la comunicación que se tendrá con el proceso (a través del correo electrónico (Equidadygenero@cuc.edu.co) y recordando que la Universidad de la Costa está como red de apoyo en lo que requiera.
                    </Typography>
                    <Typography variant="caption" style={{ display: 'block' }}>
                        Notifico que he leído con detalle la trascripción de la anterior entrevista y estoy de acuerdo con lo allí escrito. El documento tiene una extensión de ( {safeValue('numero_cuartillas')} ) cuartillas debidamente numeradas.
                    </Typography>
                </Paper>
            </Grid>

        </Grid>
    );
}

export const Formulario005Steps = [
    {
        label: 'Datos Personales',
        render: (props) => <DatosEntrevistado {...props} solicitud={props.solicitud} />
    },
    {
        label: 'Relato de Hechos',
        render: (props) => <RelatoHechos {...props} />
    }
];